import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import {
  billingPeriodOptions,
  buildEmptyPlan,
  buildPlanPayload,
  mapPlanToForm,
  planScalarFields,
  planToggleFields,
} from "./subscriptionPlanFormUtils";

function SubscriptionPlanEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [savingPlan, setSavingPlan] = useState(false);
  const [planForm, setPlanForm] = useState(buildEmptyPlan());
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
    setTimeout(() => {
      setNotification(false);
      setMessage("");
      setNotiColor("success");
    }, 2500);
  };

  useEffect(() => {
    const loadPlan = async () => {
      try {
        if (init() !== "success") {
          window.location.assign("/signin");
          return;
        }

        if (!isEditMode) {
          setPlanForm(buildEmptyPlan());
          setLoading(false);
          return;
        }

        setLoading(true);
        const response = await axios.get(Constants.getUrls.getSubscriptionPlans);
        const currentPlan = (response?.data?.data || []).find((item) => item?._id === id);

        if (!currentPlan) {
          showNotice("Subscription plan not found.", "error");
          navigate("/subscriptions", { replace: true });
          return;
        }

        setPlanForm(mapPlanToForm(currentPlan));
      } catch (error) {
        showNotice(error?.response?.data?.message || "Unable to load subscription plan.", "error");
      } finally {
        setLoading(false);
      }
    };

    loadPlan();
  }, [id, isEditMode, navigate]);

  const handlePlanField = (key, value) => {
    setPlanForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleProviderField = (key, value) => {
    setPlanForm((prev) => ({
      ...prev,
      provider_product_ids: {
        ...prev.provider_product_ids,
        [key]: value,
      },
    }));
  };

  const handleSubmitPlan = async (event) => {
    event.preventDefault();
    try {
      if (init() !== "success") {
        window.location.assign("/signin");
        return;
      }

      setSavingPlan(true);
      const payload = buildPlanPayload(planForm);

      if (isEditMode) {
        await axios.patch(`${Constants.putUrls.updateSubscriptionPlan}${id}`, payload);
        showNotice("Subscription plan updated successfully.");
      } else {
        await axios.post(Constants.postUrls.createSubscriptionPlan, payload);
        showNotice("Subscription plan created successfully.");
      }

      setTimeout(() => {
        navigate("/subscriptions");
      }, 500);
    } catch (error) {
      showNotice(
        error?.response?.data?.message ||
          `Unable to ${isEditMode ? "update" : "create"} subscription plan.`,
        "error"
      );
    } finally {
      setSavingPlan(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} xl={10}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={1.5}
                >
                  <MDBox>
                    <MDTypography variant="h6" color="white">
                      {isEditMode ? "Edit Subscription Plan" : "Create Subscription Plan"}
                    </MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.85}>
                      Manage pricing, plan benefits, and visibility from a separate editor page.
                    </MDTypography>
                  </MDBox>
                  <MDButton color="white" variant="outlined" onClick={() => navigate("/subscriptions")}>
                    Back to Listing
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                {loading ? (
                  <MDTypography variant="button" color="text">
                    Loading subscription plan...
                  </MDTypography>
                ) : (
                  <MDBox component="form" onSubmit={handleSubmitPlan}>
                    <Grid container spacing={2}>
                      {planScalarFields.map(([key, label]) => {
                        if (key === "billing_period") {
                          return (
                            <Grid item xs={12} md={6} key={key}>
                              <FormControl fullWidth>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                  value={planForm.billing_period}
                                  label={label}
                                  onChange={(event) =>
                                    handlePlanField("billing_period", event.target.value)
                                  }
                                >
                                  {billingPeriodOptions.map((option) => (
                                    <MenuItem value={option.value} key={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          );
                        }

                        if (key === "description" || key === "refund_policy" || key === "feature_list") {
                          return (
                            <Grid item xs={12} key={key}>
                              <TextField
                                fullWidth
                                label={label}
                                value={planForm[key]}
                                multiline
                                minRows={key === "feature_list" ? 2 : 3}
                                onChange={(event) => handlePlanField(key, event.target.value)}
                              />
                            </Grid>
                          );
                        }

                        return (
                          <Grid item xs={12} md={6} key={key}>
                            <TextField
                              fullWidth
                              label={label}
                              value={planForm[key]}
                              onChange={(event) => handlePlanField(key, event.target.value)}
                            />
                          </Grid>
                        );
                      })}

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Web Product ID"
                          value={planForm.provider_product_ids.web}
                          onChange={(event) => handleProviderField("web", event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Android Product ID"
                          value={planForm.provider_product_ids.android}
                          onChange={(event) => handleProviderField("android", event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="iOS Product ID"
                          value={planForm.provider_product_ids.ios}
                          onChange={(event) => handleProviderField("ios", event.target.value)}
                        />
                      </Grid>

                      {planToggleFields.map(([key, label]) => (
                        <Grid item xs={12} md={6} key={key}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(planForm[key])}
                                onChange={(event) => handlePlanField(key, event.target.checked)}
                              />
                            }
                            label={label}
                          />
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <MDBox display="flex" gap={1.5} flexWrap="wrap">
                          <MDButton type="submit" color="success" disabled={savingPlan}>
                            {savingPlan
                              ? isEditMode
                                ? "Updating..."
                                : "Saving..."
                              : isEditMode
                              ? "Update Plan"
                              : "Create Plan"}
                          </MDButton>
                          <MDButton
                            type="button"
                            color="secondary"
                            variant="outlined"
                            onClick={() => navigate("/subscriptions")}
                          >
                            Cancel
                          </MDButton>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime=""
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default SubscriptionPlanEditor;
